import { useEffect, useState, useCallback } from "react";
import { Joyride, STATUS, EVENTS } from "react-joyride";
import { useAuth } from "../context/AuthContext";

/**
 * Per-feature, per-user, one-shot Joyride tour.
 *
 * Auto-runs the first time a user lands on a page that mounts this component
 * for the given `featureId`. After completion or skip, sets a localStorage
 * flag so it never auto-fires again. A user can restart any individual tour
 * by visiting Settings → Help & onboarding (TODO if needed) and clearing the
 * flag, but for v1 once-per-user is the contract.
 *
 * Usage:
 *   <FeatureTour
 *     featureId="email-signature"
 *     steps={[
 *       { target: '[data-testid="email-signature"]', title: "...", content: "..." },
 *       ...
 *     ]}
 *   />
 *
 * Props:
 *   - featureId: stable string identifier; used as part of the storage key.
 *   - steps: array of Joyride step objects.
 *   - waitForSelector: optional CSS selector that must exist before the tour
 *     fires. Useful for pages that render asynchronously after data fetch.
 *   - delayMs: optional ms-delay before kick-off (default 600 ms).
 */
const STORAGE_PREFIX = "rv_feature_tour_done_";

export default function FeatureTour({
  featureId,
  steps,
  waitForSelector,
  delayMs = 600,
}) {
  const { user } = useAuth();
  const [run, setRun] = useState(false);

  useEffect(() => {
    if (!user || !featureId || !steps?.length) return;
    const key = STORAGE_PREFIX + user.id + "_" + featureId;
    if (localStorage.getItem(key)) return;

    let cancelled = false;
    let attempts = 0;
    const tryStart = () => {
      if (cancelled) return;
      // Optional gate — wait for a specific selector to materialise so the
      // first step doesn't get rejected as TARGET_NOT_FOUND.
      if (waitForSelector && !document.querySelector(waitForSelector)) {
        if (attempts++ < 12) {
          setTimeout(tryStart, 500);
          return;
        }
        // Bail out silently — the page took too long to render.
        return;
      }
      setRun(true);
    };
    const t = setTimeout(tryStart, delayMs);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [user, featureId, steps, waitForSelector, delayMs]);

  const handleCallback = useCallback(
    (data) => {
      const { status } = data;
      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        if (user && featureId) {
          localStorage.setItem(STORAGE_PREFIX + user.id + "_" + featureId, "1");
        }
        setRun(false);
      }
      if (data.type === EVENTS.TARGET_NOT_FOUND) {
        // eslint-disable-next-line no-console
        console.warn(`[FeatureTour:${featureId}] target not found`, data.step);
      }
    },
    [user, featureId]
  );

  if (!user || !steps?.length) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton
      showProgress
      scrollToFirstStep
      disableOverlayClose
      callback={handleCallback}
      locale={{
        back: "Back",
        close: "Close",
        last: "Got it",
        next: "Next",
        skip: "Skip",
      }}
      styles={{
        options: {
          primaryColor: "#0f172a",
          textColor: "#0f172a",
          backgroundColor: "#ffffff",
          arrowColor: "#ffffff",
          overlayColor: "rgba(15, 23, 42, 0.55)",
          zIndex: 10000,
        },
        tooltipContainer: { textAlign: "left" },
        tooltipTitle: {
          fontFamily: "Outfit, sans-serif",
          fontSize: "15px",
          fontWeight: 700,
        },
        tooltipContent: {
          fontSize: "13px",
          lineHeight: "1.5",
          color: "#475569",
        },
        buttonNext: {
          borderRadius: 2,
          fontFamily: "monospace",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontSize: "11px",
          padding: "8px 14px",
        },
        buttonBack: {
          color: "#64748b",
          fontFamily: "monospace",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontSize: "11px",
        },
        buttonSkip: {
          color: "#94a3b8",
          fontFamily: "monospace",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontSize: "11px",
        },
      }}
    />
  );
}
